import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl } from '@angular/forms';
import { IconNames } from '@app/shared/enums/icon-names';
import { Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass, NgStyle } from '@angular/common';

/**
 *
 * The checkbox is meant to be used for a form control that allows the user to select multiple options from a set of options.
 *
 * Usage example:
 * @example
 * <app-ice-select-box label="myLabel" value="myValue" control="myFormControl" />
 *
 * Example with custom header;
 * @example
 * <app-ice-select-box label="myLabel" value="myValue" control="myFormControl">
 *  <div header>
 *    <span>Custom Header</span>
 *  </div>
 * </app-ice-select-box>
 *
 * Example with custom body;
 * @example
 * <app-ice-select-box label="myLabel" value="myValue" control="myFormControl">
 *  <div>
 *    <span>Custom Body</span>
 *  </div>
 * </app-ice-select-box>
 *
 * Example with custom inline header;
 * @example
 * <app-ice-select-box label="myLabel" value="myValue" control="myFormControl">
 * <div inlineHeaderContent>
 *  <span>Custom Inline Header</span>
 * </div>
 * </app-ice-select-box>
 *
 * Example with checkbox type;
 * @example
 * <app-ice-select-box label="myLabel" value="myValue" control="myFormControl" type="checkbox" />
 */
@Component({
  selector: 'app-ice-select-box',
  templateUrl: './ice-select-box.component.html',
  styleUrl: './ice-select-box.component.scss',
  imports: [NgClass, NgStyle, MatTooltip, MatIcon],
})
export class IceSelectBoxComponent implements OnInit, OnDestroy, AfterViewInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);

  @Input({ required: true }) label: string;
  @Input({ required: false }) type: 'checkbox' | 'radio' = 'radio';
  @Input({ required: true }) value: string | number | boolean;
  @Input({ required: true }) control: AbstractControl;
  @Input({ required: false }) disabled: boolean = false;
  @Input({ required: false }) readonly: boolean = false;
  @Input({ required: false }) infoText: string;
  @Input({ required: false }) hideOverflow: boolean = true;
  @Input({ required: false }) helpLabel: string;
  @Input({ required: false }) hasOverwriteError: boolean = false;
  radioTestId = input<string>();
  checkboxTestId = input<string>();
  reserveSpaceForInfoText = input<boolean>(false);

  @ViewChild('headerContent') headerContent: ElementRef;
  @ViewChild('bodyContent') bodyContent: ElementRef;
  @ViewChild('checkboxInput') checkboxInput: ElementRef;
  @ViewChild('inlineHeaderContent') inlineHeaderContent: ElementRef;

  subscriptions$: Subscription[] = [];
  hasCustomHeader: boolean = false;
  hasBodyContent: boolean = false;
  hasInlineHeaderContent: boolean = false;

  private bodyContentObserver: MutationObserver;
  private headerContentObserver: MutationObserver;

  IconNames = IconNames;

  onClick() {
    const nativeElement = this.checkboxInput?.nativeElement;
    if (this.disabled || this.readonly) return;
    if (this.isRadio) {
      this.control?.setValue(this.value);
    } else {
      if (!this.control) return;
      if (this.control instanceof FormArray) {
        const currentValue = this.control.value || [];
        const valueIndex = currentValue.indexOf(this.value);
        if (valueIndex > -1) {
          this.control.removeAt(valueIndex);
          nativeElement && (nativeElement.checked = false);
        } else {
          this.control.push(new FormControl(this.value));
          nativeElement && (nativeElement.checked = true);
        }
      } else {
        const booleanControl = this.control as FormControl<boolean>;
        const currentValue = !!booleanControl.value;
        const newValue = !currentValue;
        booleanControl.setValue(newValue);
        nativeElement && (nativeElement.checked = newValue);
      }
    }
    this.control?.markAsDirty();
    this.control?.markAsTouched();
  }

  onKeydown(event: KeyboardEvent) {
    if (this.disabled || this.readonly) return;
    if (event.key === 'Enter' || event.key === ' ') {
      this.onClick();
      event.preventDefault();
    }
  }

  get tabIndex(): number {
    return this.disabled || this.readonly ? -1 : 0;
  }

  @HostBinding('class.has-info-text') get hasInfoText() {
    return !!this.infoText;
  }

  @HostBinding('class.selected') get isSelected() {
    return this.selected;
  }

  @HostBinding('class.readonly') get getReadonly() {
    return this.readonly;
  }
  @HostBinding('class.disabled') get getDisabled() {
    return this.disabled;
  }
  @HostBinding('class.has-checkbox') get hasCheckbox() {
    return this.isCheckbox;
  }
  @HostBinding('class.has-radio') get hasRadio() {
    return this.isRadio;
  }
  @HostBinding('class.has-body-content') get hasBody() {
    return this.hasBodyContent && this.selected;
  }

  get selected(): boolean {
    if (this.isRadio) {
      return this.control?.value === this.value;
    } else if (this.isCheckbox) {
      if (this.control instanceof FormArray) {
        return this.control?.value.includes(this.value);
      }
      return !!this.control?.value;
    }
    return false;
  }

  get isRadio(): boolean {
    return this.type === 'radio';
  }

  get isCheckbox(): boolean {
    return this.type === 'checkbox';
  }

  ngOnInit(): void {
    if (this.control) {
      this.subscriptions$.push(
        this.control.valueChanges.subscribe(() => {
          this.changeDetectorRef.detectChanges();
        })
      );
    }
  }

  ngAfterViewInit(): void {
    this.hasCustomHeader = this.headerContent && this.headerContent.nativeElement.children.length > 0;

    this.hasBodyContent = this.bodyContent && this.bodyContent.nativeElement.children.length > 0;

    if (this.hasBodyContent) {
      this.renderer.addClass(this.bodyContent.nativeElement.children[0], 'content-section');
      this.renderer.addClass(this.el.nativeElement, 'has-body-content');
    }
    this.hasInlineHeaderContent = this.inlineHeaderContent && this.inlineHeaderContent.nativeElement.children.length > 0;

    if (this.isCheckbox && this.selected && this.checkboxInput) {
      this.checkboxInput.nativeElement.checked = true;
    }

    this.bodyContentObserver = new MutationObserver(() => {
      this.updateBodyState();
    });

    this.bodyContentObserver.observe(this.bodyContent.nativeElement, { childList: true });

    this.headerContentObserver = new MutationObserver(() => {
      this.updateHeaderState();
    });

    this.headerContentObserver.observe(this.headerContent.nativeElement, { childList: true });

    this.changeDetectorRef.detectChanges();
  }

  private updateBodyState() {
    this.hasBodyContent = this.bodyContent && this.bodyContent.nativeElement.children.length > 0;

    if (!this.hasBodyContent) {
      this.control?.markAsPristine();
      this.renderer.removeClass(this.el.nativeElement, 'has-body-content');
    } else {
      this.renderer.addClass(this.el.nativeElement, 'has-body-content');
    }
    this.changeDetectorRef.detectChanges();
  }

  private updateHeaderState() {
    this.hasCustomHeader = this.headerContent && this.headerContent.nativeElement.children.length > 0;
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(subscription => subscription.unsubscribe());
  }
}
